import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, Kicker, Tag } from "@/components/Reveal";
import { Field, TextArea, SubmitButton } from "@/components/FormBits";
import { submitReferral } from "@/lib/api";

const WHEN = [
  "is disengaging from school", "is repeatedly suspended", "is struggling around family contact",
  "is experiencing placement instability", "has multiple services involved",
  "is difficult to engage through office-based services", "needs culturally responsive mentoring",
  "needs support communicating their views", "is experiencing substance-use concerns",
  "needs identity and strengths-based work", "would benefit from movement or community-based engagement",
];

const REFERRERS = ["Families", "Kinship carers", "Foster carers", "Schools", "Aboriginal organisations", "NGOs", "Government agencies", "Child protection", "Youth justice", "Community services"];

const EMPTY = {
  child_name: "", age: "", contact_details: "", referrer_name: "", organisation: "",
  relationship: "", reason: "", whats_happening: "", support_areas: "", current_services: "",
  cultural_considerations: "", accessibility: "", submitter_email: "", consent: false, preferred_contact: "",
};

const Referrals = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.child_name.trim() || !form.referrer_name.trim() || !form.reason.trim()) {
      toast.error("Please complete the young person's name, your name, and the reason for referral.");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm consent before sending.");
      return;
    }
    setBusy(true);
    try {
      await submitReferral(form);
      toast.success("Referral received. Thank you — we'll yarn with you about the best way forward.");
      setForm(EMPTY);
    } catch {
      toast.error("Something went wrong sending that. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="referrals-page">
      <PageHero
        kicker="Make a Referral"
        title={"Tell us what's happening.\nWe'll find the fit together."}
        lede="Referrals are based on what is happening in a child or family's life — you don't need to perfectly select a program. Not sure? Yarn with us instead."
      >
        <div className="mt-8 flex flex-wrap gap-3">
          {REFERRERS.map((r) => (
            <span key={r} className="rounded-full bg-creamdark border border-ochre/35 px-4 py-2 text-[13px] font-semibold text-earth">{r}</span>
          ))}
        </div>
      </PageHero>

      <section className="px-6 py-14 sm:py-20 bg-cream">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <Kicker>For professionals & services</Kicker>
              <h2 className="font-display font-light text-3xl sm:text-4xl leading-[1.1] text-charcoal">
                When might Ngawilyi help?
              </h2>
              <p className="mt-5 text-earth/85 leading-relaxed">When a child or young person…</p>
              <ul className="mt-6 space-y-3">
                {WHEN.map((w) => (
                  <li key={w} className="flex gap-3 items-start text-[15px] text-earth">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />
                    {w}
                  </li>
                ))}
              </ul>
              <div className="mt-10 rounded-[32px] bg-creamdark border border-ochre/30 p-7 flex items-center gap-5">
                <div className="shrink-0 rounded-2xl bg-terracotta/15 p-4"><FileDown className="h-6 w-6 text-terracotta" /></div>
                <div>
                  <p className="font-semibold text-charcoal">Ngawilyi Referral & Service Information Pack</p>
                  <p className="text-sm text-earth/75 mt-1">Downloadable pack — coming soon. <Tag tone="ochre">Coming Soon</Tag></p>
                </div>
              </div>
              <p className="mt-8 text-earth/85 leading-relaxed">
                Not sure which program fits?{" "}
                <Link data-testid="referral-yarn-link" to="/contact" className="group inline-flex items-center gap-1 font-bold text-terracotta">
                  Yarn With Us <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="rounded-[48px] bg-creamdark border border-ochre/25 p-8 sm:p-12 grain relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 bg-terracotta/10 organic-blob blur-2xl" />
              <form data-testid="referral-form" onSubmit={onSubmit} className="relative grid sm:grid-cols-2 gap-6">
                <Field label="Child / young person's name" testid="ref-child-name-input" value={form.child_name} onChange={set("child_name")} required />
                <Field label="Age" testid="ref-age-input" optional value={form.age} onChange={set("age")} />
                <Field label="Contact details" testid="ref-contact-input" optional value={form.contact_details} onChange={set("contact_details")} placeholder="Where appropriate" />
                <Field label="Your name (referrer)" testid="ref-referrer-input" value={form.referrer_name} onChange={set("referrer_name")} required />
                <Field label="Organisation" testid="ref-organisation-input" optional value={form.organisation} onChange={set("organisation")} />
                <Field label="Relationship to young person" testid="ref-relationship-input" optional value={form.relationship} onChange={set("relationship")} />
                <div className="sm:col-span-2">
                  <TextArea label="Reason for referral" testid="ref-reason-textarea" value={form.reason} onChange={set("reason")} required />
                </div>
                <div className="sm:col-span-2">
                  <TextArea label="What's happening currently" testid="ref-happening-textarea" optional value={form.whats_happening} onChange={set("whats_happening")} />
                </div>
                <TextArea label="Areas where support may help" testid="ref-support-textarea" optional value={form.support_areas} onChange={set("support_areas")} placeholder="e.g. school, placement, family contact, identity" />
                <TextArea label="Current services involved" testid="ref-services-textarea" optional value={form.current_services} onChange={set("current_services")} />
                <TextArea label="Cultural considerations" testid="ref-cultural-textarea" optional value={form.cultural_considerations} onChange={set("cultural_considerations")} />
                <TextArea label="Accessibility considerations" testid="ref-accessibility-textarea" optional value={form.accessibility} onChange={set("accessibility")} />
                <Field label="Preferred contact method" testid="ref-preferred-input" optional value={form.preferred_contact} onChange={set("preferred_contact")} placeholder="Phone, email, text…" />
                <Field label="Your email — for a confirmation copy" testid="ref-email-input" type="email" optional value={form.submitter_email} onChange={set("submitter_email")} placeholder="We'll send a warm confirmation" />
                <label className="sm:col-span-2 flex items-start gap-4 rounded-3xl bg-cream border border-ochre/35 px-6 py-5 cursor-pointer" data-testid="ref-consent-wrap">
                  <input data-testid="ref-consent-checkbox" type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 h-5 w-5 accent-[#C05746]" />
                  <span className="text-sm leading-relaxed text-earth">
                    I confirm that I have appropriate consent to make this referral, and understand that Ngawilyi is
                    privacy-first and consent-based — the young person's story belongs to them.
                  </span>
                </label>
                <div className="sm:col-span-2 pt-2">
                  <SubmitButton testid="ref-submit-btn" busy={busy}>Send Referral</SubmitButton>
                </div>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 bg-cream">
        <Reveal>
          <div className="max-w-7xl mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-5">Not a service-funded referral?</p>
            <div className="flex flex-wrap gap-3">
              {[["Private Supports — no referral needed", "/private-supports"], ["NDIS Supports", "/ndis-supports"], ["Request Support — find your pathway", "/request-support"], ["Not sure? Yarn With Us", "/contact"]].map(([l, to]) => (
                <Link key={to} data-testid={`referrals-xlink-${to.slice(1)}`} to={to} className="rounded-full bg-creamdark border border-ochre/30 px-5 py-2.5 text-sm font-semibold text-earth transition-all duration-300 hover:border-terracotta hover:text-terracotta">
                  {l}
                </Link>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Referrals;
